import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";
import { isMiniapp, miniappLaunch, saveMiniappSession, readAuthUser, saveAuthUser, clearAuthUser } from './miniappSession';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readAuthUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verify either the web cookie or Telegram's short-lived session.
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        saveAuthUser(r.data);
      })
      .catch(() => {
        clearAuthUser();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const miniapp = isMiniapp();
    const { data } = await api.post(miniapp ? '/telegram-control/miniapp/login' : '/auth/login',
      miniapp ? { email, password, init_data:miniappLaunch(), connect_telegram:true } : { email, password });
    if (miniapp) saveMiniappSession(data);
    saveAuthUser(data.user);
    setUser(data.user);
    const connection = data.telegram_notifications;
    if (connection?.notifications) toast.success('Telegram terhubung. Notifikasi FishKa aktif.');
    else if (connection) {
      const reasons = {
        muted:'Telegram terhubung. Notifikasi masih dimatikan; aktifkan melalui /settings di FishKa.',
        start_required:'Login berhasil. Tekan Start di FishKa, lalu masuk kembali untuk mengaktifkan notifikasi.',
        owner_conflict:'Login berhasil. Akun ini sudah terhubung ke Telegram lain. Putuskan penghubung lama di Notifikasi jika ingin menggantinya.',
        telegram_conflict:'Login berhasil. Telegram ini sudah terhubung ke akun FishIt lain. Putuskan penghubung lama terlebih dahulu.',
        unavailable:'Login berhasil. FishKa sedang belum siap; notifikasi belum terhubung.',
        changed:'Login berhasil. Penghubung sedang berubah; periksa halaman Notifikasi sebelum mencoba lagi.',
      };
      toast.warning(reasons[connection.status] || 'Login berhasil. Periksa penghubung di halaman Notifikasi.',{duration:10000});
    }
    return data.user;
  }, []);

  const register = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/register", { email, password });
    saveAuthUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore network errors on logout */
    }
    clearAuthUser();
    localStorage.removeItem("fishit_account");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.get('/auth/me');
    setUser(data);
    saveAuthUser(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
